import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { adminDesignsQuery } from '@/queries';
import { Callout, EmptyState, Field, buttonClass, inputClass } from '@/components/ui';

/**
 * The ready-made design library. AC-5.2.
 *
 * These are the designs offered in the studio and to the app. Artwork lives in
 * the client's own S3 bucket under `designs/ready/{id}.png`, and is written only
 * through the presigned upload path. **Not R2**: R-32 found three documents
 * naming three different object stores, and §12.7 settled it as S3.
 */

/**
 * Adding a design. R-279.
 *
 * The library was read only, so the publish flow could offer nothing and the
 * whole publishing path stopped at a table only a developer could fill.
 *
 * The artwork and the name go in one request. Two steps would leave a design
 * with no image if the second failed, and the publish preview would offer a
 * broken picture to somebody about to put it on a shirt.
 */
function AddDesign() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.set('name', name);
      if (file) form.set('file', file);

      const res = await fetch('/api/designs', {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not add it.');
      return body?.message ?? 'Added.';
    },
    onSuccess: (message) => {
      setSaid(message);
      setName('');
      setFile(null);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'designs'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  if (!open) {
    return (
      <div>
        <button type="button" className={buttonClass('primary', 'sm')} onClick={() => setOpen(true)}>
          Add a design
        </button>
        {said && <Callout tone="ok" title={said} className="mt-3" />}
      </div>
    );
  }

  return (
    <form
      className="rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); setSaid(null); add.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor="d-name" hint="What the studio calls it">
          <input
            id="d-name" className={inputClass} required value={name}
            placeholder="Obsessed script, white"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Artwork" htmlFor="d-file" hint="PNG, JPG, WEBP or SVG, up to 15 MB">
          <input
            id="d-file" type="file" className={inputClass} required
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
      </div>

      {said && <Callout tone="danger" title={said} className="mt-3" />}

      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={add.isPending}>
          {add.isPending ? 'Uploading' : 'Add the design'}
        </button>
        <button
          type="button" className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setSaid(null); }}
        >
          Cancel
        </button>
      </div>

      {/* Print artwork is large on purpose, and somebody will try to shrink it
          to fit. Say what the size is for. */}
      <p className="mt-3 text-[12.5px] text-muted">
        Send the print file, not a preview. A chest print at 300dpi is a big image,
        and one compressed to look small on screen prints badly.
      </p>
    </form>
  );
}


/**
 * Taking a design out of the studio's list, and putting it back.
 *
 * `active` is what the publish flow reads, so this is the control that decides
 * whether a design can reach a shirt. Nothing is deleted: a design that has
 * been published on a product must keep its artwork at the same address, or
 * every product carrying it shows a broken image.
 */
function DesignControls({ design }: { design: { id: string; name: string; active: boolean } }) {
  const qc = useQueryClient();
  const [said, setSaid] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const res = await fetch(`/api/designs/${design.id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not save it.');
    },
    onSuccess: () => {
      setSaid(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'designs'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  return (
    <div className="mt-3">
      <button
        type="button"
        className={buttonClass('secondary', 'sm')}
        disabled={save.isPending}
        onClick={() => save.mutate({ active: !design.active })}
      >
        {design.active ? 'Take out of the studio' : 'Put back in the studio'}
      </button>
      {said && <Callout tone="danger" title={said} className="mt-2" />}
    </div>
  );
}

export default function AdminDesignsPage() {
  const { designs, brands } = useSuspenseQuery(adminDesignsQuery).data;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Design library</h1>
        <p className="mt-1 max-w-[62ch] text-[15px] text-muted">
          Ready-made designs the studio offers customers, and the brand accents the
          storefront themes with.
        </p>
      </div>

      <section aria-labelledby="ready-heading">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 id="ready-heading" className="text-lg font-bold tracking-[-0.02em]">
            Ready-made designs
          </h2>
          <AddDesign />
        </div>
        {designs.length === 0 ? (
          <EmptyState
            title="No ready-made designs yet"
            body="These are the designs a customer can pick in the studio without sending
              artwork. Upload one and it appears here and in the studio."
          />
        ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {designs.map((d) => (
            <figure
              key={d.id}
              className={'rounded-[10px] border border-line bg-panel p-4 '
                + (d.active ? '' : 'opacity-60')}
            >
              <div className="checkerboard rounded-[10px] border border-line p-4">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={d.image_url}
                  alt={d.name}
                  className="mx-auto aspect-square w-full object-contain"
                />
              </div>
              <figcaption className="mt-3">
                <p className="text-[15px] font-bold">{d.name}</p>
                <p className="mt-0.5 break-all font-mono text-[13px] text-muted">
                  {d.image_url}
                </p>
                <DesignControls design={d} />
              </figcaption>
            </figure>
          ))}
        </div>
        )}
      </section>

      <section className="mt-12" aria-labelledby="brands-heading">
        <h2 id="brands-heading" className="mb-4 text-lg font-bold tracking-[-0.02em]">
          Brands
        </h2>
        <ul className="divide-y divide-line overflow-hidden rounded-[10px] border border-line bg-panel">
          {brands.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-4 px-4 py-4">
              <span
                aria-hidden
                className="size-9 shrink-0 rounded-[8px] border border-ink/15"
                style={{ background: b.accent_hex }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-bold">{b.name}</span>
                <span className="text-sm text-muted">{b.tagline}</span>
              </span>
              <span className="font-mono text-sm text-muted">{b.slug}</span>
              <span className="font-mono text-sm text-muted">{b.accent_hex}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
